import { CommonModule, isPlatformBrowser } from '@angular/common';
import { AfterViewInit, Component, OnInit, Inject, PLATFORM_ID } from '@angular/core';
import { Router, RouterModule, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { AuthService } from '@auth0/auth0-angular';
import * as feather from 'feather-icons';
import { environment } from '../environments/environment';
import { ToggleComponent } from './components/toggle/toggle.component';
import { routeUrls } from './constants/constants';
import { AlertBannerComponent } from './components/alert-banner/alert-banner.component';
import { AlertService } from './services/alert.service';
import { LoadingService } from './services/loading.service';
import { UserService } from './services/user.service';
import { SeoService } from './services/seo.service';
import { StructuredDataService } from './services/structured-data.service';
import { DOCUMENT } from '@angular/common';
import { isMobileWebView } from './utilities/mobile.utils';
import { SmartAppBannerService } from './services/smart-app-banner.service';
import { clearAuthStorage } from './utilities/auth-storage';


@Component({
  standalone: true,
  imports: [CommonModule, RouterModule,ToggleComponent,AlertBannerComponent],
  selector: 'cricteams-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, AfterViewInit {
  public isFullScreenLoading = false;

  private isBrowser: boolean;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private router: Router,
    private alertService: AlertService,
    private loadingService: LoadingService,
    private userService: UserService,
    private seoService: SeoService,
    private structuredDataService: StructuredDataService,
    @Inject(DOCUMENT) private document: Document,
    public authService: AuthService,
    private smartAppBanner: SmartAppBannerService,
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }
  public message!: string;
  public isSuccess!: boolean;
  public isError!: boolean;

  title = 'CrickTeams - Cricket Team Management Platform';
  
  ngAfterViewInit() {
    if (this.isBrowser) {
      feather.replace();
    }
  }
  
  ngOnInit() {
    // Add noindex meta tag in non-production environments to prevent indexing
    if (!environment.production) {
      const metaRobots = this.document.createElement('meta');
      metaRobots.name = 'robots';
      metaRobots.content = 'noindex, nofollow';
      this.document.head.appendChild(metaRobots);
    }
    
    // Initialize SEO defaults
    this.seoService.setDefaultTags();
    
    // Add global organization structured data
    this.structuredDataService.addOrganization({
      name: 'CrickTeams',
      url: 'https://www.crickteams.com',
      logo: 'https://www.crickteams.com/assets/images/logo.png',
    });
    
    // Add website schema with search
    this.structuredDataService.addWebSite({
      name: 'CrickTeams',
      url: 'https://www.crickteams.com',
    });
    
    if (!this.isBrowser) {
      return; // Skip browser-only initialization during SSR/prerender
    }

    // Safari Smart App Banner: keep app-argument on the current page (browser-only, one subscription).
    this.smartAppBanner.start(this.router);

    // Check for persisted Auth0 error message and display it
    const persistedError = sessionStorage.getItem('auth0_error_message');
    if (persistedError) {
      sessionStorage.removeItem('auth0_error_message');
      this.alertService.showError(persistedError);
    }

    if (environment.enableRecaptcha && !isMobileWebView()) {
      this.loadRecaptchaScript();
    }
    if (environment.enableGTM && !isMobileWebView()) {
      this.loadGoogleTagManager();
    }
    if (environment.facebookPixelId && !isMobileWebView()) {
      this.loadFacebookPixel(environment.facebookPixelId);
      // Track PageView on SPA route changes
      this.router.events
        .pipe(filter((event: any) => event instanceof NavigationEnd))
        .subscribe(() => {
          (window as any).fbq?.('track', 'PageView');
        });
    }

    // Only handle Auth0 redirect if we have actual OAuth callback params
    const urlParams = new URLSearchParams(window.location.search);
    const hasAuthCode = urlParams.has('code') && urlParams.has('state');
    const hasAuthError = urlParams.has('error');
    
    // Handle Auth0 errors from URL FIRST (before SDK callback)
    if (hasAuthError) {
      const urlError = urlParams.get('error');
      const urlErrorDescription = urlParams.get('error_description');
      
      console.error('Auth0 error in callback URL:', {
        error: urlError,
        errorDescription: urlErrorDescription
      });
      
      // Determine error message based on error code and description
      let errorMessage = 'Authentication failed. Please try again.';
      
      if (urlErrorDescription === 'user_not_registered' || urlError === 'user_not_registered') {
        errorMessage = 'Account not found. You must complete club registration or join a club before logging in.';
      } else if (urlError === 'access_denied') {
        // Check if description contains our custom error
        if (urlErrorDescription?.includes('user_not_registered')) {
          errorMessage = 'Account not found. You must complete club registration or join a club before logging in.';
        } else {
          errorMessage = urlErrorDescription || 'Access denied. Please check your credentials and try again.';
        }
      } else if (urlError === 'missing_transaction') {
        // Handle missing_transaction from URL params (when denied users retry login)
        errorMessage = 'Unable to complete login. Please ensure you have completed club registration or received an invite.';
      } else if (urlErrorDescription) {
        errorMessage = urlErrorDescription;
      }
      
      // Store error message in sessionStorage to persist across logout redirect
      sessionStorage.setItem('auth0_error_message', errorMessage);
      
      // Logout to clear Auth0 session, then redirect to home
      clearAuthStorage();
      this.authService.logout({
        logoutParams: {
          returnTo: window.location.origin
        }
      });
      return; // Exit early, don't try SDK callback
    } 
    
    // Handle successful auth code callback
    if (hasAuthCode) {
      this.authService.handleRedirectCallback().subscribe({
        next: (result) => {
          if (result?.appState?.['returnTo']) {
            sessionStorage.setItem('targetUrl', result.appState['returnTo']);
          }
          const targetRoute = result?.appState?.target || `/${routeUrls.PREDASHBOARD}`;
          this.router.navigate([targetRoute]);
        },
        error: (err) => {
          // Only handle SDK errors here (not URL param errors which are handled above)
          const errorCode = err?.error || '';
          const errorDesc = err?.error_description || err?.message || '';
          
          // Log the error but don't take action - Auth0 SDK sometimes returns
          // missing_transaction errors even when authentication succeeds
          console.warn('Auth0 SDK error (ignored):', {
            error: errorCode,
            errorDescription: errorDesc
          });
        }
      });
    }

    this.alertService.message.subscribe((message: string) => {
      this.message = message;
    })
    this.alertService.isSuccess.subscribe((isSuccess: boolean) => {
      this.isSuccess = isSuccess;
    })
    this.alertService.isError.subscribe((isError: boolean) => {
      this.isError = isError;
    })
  }
  private loadRecaptchaScript() {
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${environment.recaptchaSiteKey}`;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  }

  private loadGoogleTagManager() {
    (function (w: any, d: Document, s: string, l: string, i: string) {
      w[l] = w[l] || [];
      w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
      const f = d.getElementsByTagName(s)[0];
      const j = d.createElement(s) as HTMLScriptElement;
      const dl = l !== 'dataLayer' ? '&l=' + l : '';
      j.async = true;
      j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
      f.parentNode?.insertBefore(j, f);
    })(window, document, 'script', 'dataLayer', 'GTM-MHPX8T7B');
  }

  private loadFacebookPixel(pixelId: string) {
    const w = window as any;
    if (w.fbq) return;
    w.fbq = function (...args: any[]) {
      w.fbq.callMethod ? w.fbq.callMethod.apply(w.fbq, args) : w.fbq.queue.push(args);
    };
    if (!w._fbq) w._fbq = w.fbq;
    w.fbq.push = w.fbq;
    w.fbq.loaded = true;
    w.fbq.version = '2.0';
    w.fbq.queue = [] as any[];
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    document.head.appendChild(script);
    w.fbq('init', pixelId);
    w.fbq('track', 'PageView');
  }
}
